/**
 * **R9 §4.4 (D330) — the blocked screen: the one surface a below-floor binary renders.**
 *
 * A resident whose store build is below the document's `min_build_number` gets this instead of the
 * app: one sentence saying the version can no longer be used, and beneath it **the update action
 * the app already has** (`useAppUpdate`, D266). Nothing else — no app bar, no drawer, no back,
 * because there is nowhere to go back to.
 *
 * **A P6 surface, not a route.** It has no `RouteId`, nothing navigates to it, and it is mounted
 * from `main.tsx` in place of the whole provider tree rather than from `App`'s `renderScreen()`.
 * That is what makes §4.4's third rule true: **it reads nothing else** — no session, no gate list,
 * no API, no device cache — so it renders identically offline and cannot itself be the thing that
 * fails. Mounted inside `App`, a hung auth boot would leave a blocked resident on the splash with
 * no way to update; mounted here, the auth boot never runs. It is `screens/about/`'s shape (D136:
 * a routed screen under `screens/` that P6 owns) one step further along — the shell's own surface,
 * reading no session state and calling no API — and `infra/tests/web-ownership-map.ts` carries the
 * rule, as it must for any path under `web/src` (`AGENTS.md` §4.8).
 *
 * **The action is `useAppUpdate().apply`, not a new one.** On Android that is Play's FLEXIBLE flow
 * exactly as D266 built it — *Update* starts a background download, the row becomes *Updating…*,
 * and *Restart to finish* is a **second tap and a second consent**. On iOS it is the store link.
 * `AppUpdateType.IMMEDIATE` is refused by `native/scripts/verify-platform.mjs` and stays refused:
 * **D137 stands in full — the app never restarts itself, and a floor is not a licence to.** The
 * screen removes every other surface; it does not remove the resident's consent.
 *
 * **The label is read from P6's `drawer` namespace rather than copied.** `labelKey` is already
 * typed as a `drawer` key, and the three answers it selects between (*Update* / *Restart to
 * finish* / *Update in App Store*) are the rail's own vocabulary — a `blocked` copy of them would
 * be three strings in five catalogs that agree until one is retranslated (D269's rule, one screen
 * over). `updating` comes from the same place for the same reason, and is what `applying` renames
 * the button to; the mute is not read at all, since it governs the *report* and this screen is not
 * one (D266 §3.5).
 *
 * 🔸 **It mints no CSS of its own** and composes from classes that already exist: the brand block
 * and the prose block are `about`'s (the only prose-shaped pair in the stylesheet), and the action
 * is `.form-row > .btn-primary`, the auth screen's shape. A class of its own is owed the first time
 * this screen needs a layout the About screen does not already have.
 *
 * **D333 — the locale is the account's, read from its device-local mirror, and this file reads
 * none of it.** `AuthProvider` is what reconciles the account's locale on boot, and it is exactly
 * what this screen must not mount; so `main.tsx` seeds `I18nProvider` from
 * `i18n/localeMirror.ts` in the blocked arm only, synchronously, beside the floor read. This screen
 * reads the provider's locale like every other screen, which keeps the module graph the test below
 * pins unchanged. A device the mirror has never been written on — a fresh install, or a store
 * that refuses — still renders `DEFAULT_LOCALE`, which is what every resident read before D333.
 * The alternative that stays refused is making the block depend on a boot that can fail.
 */

import { PhoneFrame } from '../../components'
import { useTranslation } from '../../i18n/I18nProvider'
import { useAppUpdate } from '../../pwa/useAppUpdate'

export function BlockedScreen(): JSX.Element {
  const { t } = useTranslation('blocked')
  const { t: tDrawer } = useTranslation('drawer')
  const { applying, labelKey, apply } = useAppUpdate()

  return (
    <PhoneFrame>
      {/* `no-bar` because there is no `AppBar`: nothing else owns the top inset here, exactly as
          on the auth screen and the boot gate. */}
      <div className="app-screen no-bar">
        <div className="about-brand">
          <div className="about-mark" aria-hidden="true" />
          <div className="about-wordmark">SeloGate</div>
        </div>
        <div className="about-body">
          <p>{t('body')}</p>
        </div>
        <div className="form-row">
          <button
            type="button"
            className="btn-primary"
            onClick={apply}
            // The Drawer's own contract for FLEXIBLE's middle phase: renamed in place and
            // refusing a second tap, rather than a second control (D266).
            disabled={applying}
          >
            {applying ? tDrawer('updating') : tDrawer(labelKey)}
          </button>
        </div>
      </div>
    </PhoneFrame>
  )
}
